import Konva from 'konva';
import { animateMapChanges } from './map_animation.js';

export function distanceLabel(feet, name) {
  return new Konva.Shape({
    name,
    text: `${Math.round(feet * 10) / 10} ft`,
    width: 88,
    height: 24,
    listening: false,
    fill: '#17212b',
    stroke: '#ffffff',
    strokeWidth: 1,
    sceneFunc(context, shape) {
      context.beginPath();
      context.rect(0, 0, shape.width(), shape.height());
      context.closePath();
      context.fillStrokeShape(shape);
      context.font = 'bold 14px sans-serif';
      context.fillStyle = '#ffffff';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(shape.getAttr('text'), shape.width() / 2, shape.height() / 2);
    }
  });
}

// Presentation only: these nodes never enter saved shapes or the Layers list.
export class MovementDistances {
  constructor() {
    this.entries = new Map();
  }

  show(shape, motion, progress) {
    if (shape.type !== 'character') return;
    let entry = this.entries.get(shape.id);
    if (!entry) {
      entry = { label: distanceLabel(0, 'character-movement-distance') };
      this.entries.set(shape.id, entry);
    }
    if (entry.startedAt !== motion.startedAt || progress < 1) {
      clearTimeout(entry.timer);
      if (entry.frame != null) cancelAnimationFrame(entry.frame);
      entry.frame = null;
      entry.completed = false;
      entry.label.opacity(1);
    }
    entry.startedAt = motion.startedAt;
    entry.label.setAttr(
      'text',
      `${progress === 1 ? Math.round(motion.feet * 10) / 10 : Math.floor(motion.feet * progress)} ft`
    );
    this.position(entry, shape);
    if (progress === 1 && !entry.completed) {
      entry.completed = true;
      entry.timer = setTimeout(() => this.fade(shape.id, entry), 10000);
    }
  }

  position(entry, shape) {
    const node = shape.konvaShape;
    const parent = node.getParent();
    if (!parent) return;
    if (entry.label.getParent() !== parent) parent.add(entry.label);
    entry.label.position({
      x: node.x() - node.offsetX() + node.width() / 2 - entry.label.width() / 2,
      y: node.y() - node.offsetY() - entry.label.height() - 6
    });
    entry.label.moveToTop();
  }

  sync(shapes) {
    for (const [id, entry] of this.entries) {
      const shape = shapes.find((item) => item.id === id && item.type === 'character');
      if (shape) this.position(entry, shape);
      else this.remove(id);
    }
  }

  fade(id, entry) {
    const start = performance.now();
    const draw = (now) => {
      const progress = Math.max(0, Math.min(1, (now - start) / 500));
      if (progress === 1 || !animateMapChanges()) return this.remove(id);
      entry.label.opacity(1 - progress);
      entry.label.getLayer()?.batchDraw();
      entry.frame = requestAnimationFrame(draw);
    };
    draw(start);
  }

  remove(id) {
    const entry = this.entries.get(id);
    if (!entry) return;
    clearTimeout(entry.timer);
    if (entry.frame != null) cancelAnimationFrame(entry.frame);
    const layer = entry.label.getLayer();
    entry.label.destroy();
    layer?.batchDraw();
    this.entries.delete(id);
  }

  clear() {
    for (const id of this.entries.keys()) this.remove(id);
  }
}
